import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { SuperAdminLayout } from './SuperAdminLayout';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Users, CreditCard, TrendingUp, AlertTriangle,
  ArrowUpRight, Store, Activity
} from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, sub, color, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={`stat-${testId || label.toLowerCase().replace(/\s/g, '-')}`}
    className="bg-[#111827] border border-white/5 rounded-xl p-5 text-left hover:border-white/10 transition-all group w-full"
  >
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      {onClick && <ArrowUpRight className="w-4 h-4 text-gray-600 group-hover:text-gray-300 transition-colors" />}
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-xs text-gray-500 mt-1">{label}</p>
    {sub && <p className="text-[10px] text-gray-600 mt-0.5">{sub}</p>}
  </button>
);

const SuperAdminDashboard = () => {
  const { api } = useAuth();
  const { t, formatCurrency, formatDate } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await api.get('/super-admin/dashboard');
        setData(res.data);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, [api]);

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#009b3a] border-t-transparent rounded-full animate-spin" />
        </div>
      </SuperAdminLayout>
    );
  }

  const d = data || {};

  return (
    <SuperAdminLayout>
      <div className="space-y-6" data-testid="super-admin-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-xl font-bold text-white">{t('saDashboard.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('saDashboard.subtitle', { brand: 'GUYAREPAIR' })}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            icon={Building2}
            label={t('saDashboard.companies')}
            testId="entreprises"
            value={d.total_companies || 0}
            sub={t('saDashboard.activeCount', { count: d.active_companies || 0 })}
            color="bg-[#009b3a]/15 text-[#00d946]"
            onClick={() => navigate('/super-admin/companies')}
          />
          <StatCard
            icon={Users}
            label={t('saDashboard.users')}
            testId="utilisateurs"
            value={d.total_users || 0}
            color="bg-[#fedd00]/15 text-[#fedd00]"
            onClick={() => navigate('/super-admin/users')}
          />
          <StatCard
            icon={Store}
            label={t('saDashboard.shops')}
            testId="boutiques"
            value={d.total_shops || 0}
            color="bg-blue-500/15 text-blue-400"
          />
          <StatCard
            icon={CreditCard}
            label={t('saDashboard.mrr')}
            testId="mrr"
            value={formatCurrency(d.mrr || 0)}
            sub={t('saDashboard.mrrSub')}
            color="bg-emerald-500/15 text-emerald-400"
            onClick={() => navigate('/super-admin/stats')}
          />
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-[#111827] border border-white/5 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-[#009b3a]" />
              <span className="text-xs text-gray-500">{t('saDashboard.newThisMonth')}</span>
            </div>
            <p className="text-lg font-bold text-white">{d.new_subscriptions_month || 0}</p>
          </div>
          <div className="bg-[#111827] border border-white/5 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <span className="text-xs text-gray-500">{t('saDashboard.expired')}</span>
            </div>
            <p className="text-lg font-bold text-white">{d.expired_subscriptions || 0}</p>
          </div>
          <div className="bg-[#111827] border border-white/5 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="text-xs text-gray-500">{t('saDashboard.suspended')}</span>
            </div>
            <p className="text-lg font-bold text-white">{d.suspended_companies || 0}</p>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Recent Companies */}
          <div className="bg-[#111827] border border-white/5 rounded-xl">
            <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white">{t('saDashboard.recentCompanies')}</h3>
              <button onClick={() => navigate('/super-admin/companies')} className="text-xs text-[#009b3a] hover:underline">
                {t('saDashboard.viewAll')}
              </button>
            </div>
            <div className="divide-y divide-white/5">
              {(d.recent_companies || []).length === 0 ? (
                <p className="p-4 text-xs text-gray-600">{t('saDashboard.noCompanies')}</p>
              ) : (
                (d.recent_companies || []).map(c => (
                  <div key={c.id} className="px-4 py-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#009b3a]/10 flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-[#009b3a]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{c.name}</p>
                      <p className="text-xs text-gray-500">{c.subscription_plan} - {c.status}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-[#111827] border border-white/5 rounded-xl">
            <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white">{t('saDashboard.recentActivity')}</h3>
              <button onClick={() => navigate('/super-admin/logs')} className="text-xs text-[#009b3a] hover:underline">
                {t('saDashboard.viewAll')}
              </button>
            </div>
            <div className="divide-y divide-white/5">
              {(d.recent_activity || []).length === 0 ? (
                <p className="p-4 text-xs text-gray-600">{t('saDashboard.noActivity')}</p>
              ) : (
                (d.recent_activity || []).slice(0, 5).map(log => (
                  <div key={log.id} className="px-4 py-3 flex items-center gap-3">
                    <Activity className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-300 truncate">{log.action} - {log.target_type}</p>
                      <p className="text-[10px] text-gray-600">{log.details}</p>
                    </div>
                    <span className="text-[10px] text-gray-600 flex-shrink-0">
                      {formatDate(log.created_at)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminDashboard;
